import { useEffect, useState, type KeyboardEvent } from 'react';
import {
  Alert,
  Button,
  CheckGroup,
  CheckRow,
  FormField,
  FormPage,
  Input,
  Page,
  Spinner,
  StickyActionBar,
  Textarea
} from '@nexvia-logistics/ui';
import { PencilSimple } from '@nexvia-logistics/ui/icons';
import { KeyValueListStacked } from '@/shared/KeyValueListStacked';
import {
  describeServicesError,
  getMyServices,
  getServiceOptions,
  suggestServiceOption,
  updateMyServices,
  type ServiceOption
} from '@/shared/storage-services-api';
import { conditionOptionsOnly, serviceOptionsOnly } from '@/shared/service-option-kinds';

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ready' };

// Audit S6 — a save or a suggestion the API refuses with a code it documents (STORAGE_SERVICE_*,
// a deactivated option's VALIDATION_ENUM_INVALID, VALIDATION_FIELD_TOO_LONG) reads its own Spanish
// message (`describeServicesError`); anything else, and every load failure, the generic copy below.
// The backend's raw English `message` never reaches the screen.
const LOAD_ERROR_MESSAGE = 'No pudimos cargar los servicios y condiciones. Intentá de nuevo en un momento.';
const SAVE_ERROR_MESSAGE = 'No pudimos guardar los cambios. Intentá de nuevo en un momento.';
const SUGGESTION_ERROR_MESSAGE = 'No pudimos enviar tu sugerencia. Intentá de nuevo en un momento.';

/**
 * `''` -> `null` ("not set"), anything else -> the number typed. Mirrors
 * `get-storage-services.handler.ts`'s own `?? null` default: `0` is a genuine value for all three
 * default-condition fields, not the same as "nothing entered yet", so this must not fall back on
 * falsiness (`Number('0') || null` would wrongly turn a real 0 into null).
 */
function parseConditionFieldInput(raw: string): number | null {
  if (raw === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * "3 meses", "1 día", or the "not set" text — the read view of a default condition (D-5.1). A saved 0
 * reads as "not set" too: it means no condition, the way QuoteForm's `defaultAsText` reads it.
 * Display only — the saved value and the edit field keep the 0.
 */
function conditionValueText(value: number | null, singular: string, plural: string, unset: string): string {
  if (value === null || value <= 0) return unset;
  return `${value} ${value === 1 ? singular : plural}`;
}

/**
 * Milestone 4 (roadmap §4): this account's row of the services/conditions catalog —
 * `GET /storage/service-options` for the backoffice-managed list (+ "libre", the free-text
 * conditions field below it), `GET`/`PATCH /storage/me/services` for what this account currently
 * offers.
 *
 * **Fixed, 2026-10-14** (commit 664a3ca): this screen used to read through bare `GET /storage/me`,
 * which never carried this data — every account's selection silently read as empty, and a save
 * never actually persisted (the backend's real schema didn't recognize the field name sent). It
 * now calls the real, dedicated `GET`/`PATCH /storage/me/services` — see
 * `src/shared/storage-services-api.ts`'s own docblock for the wire-level field mapping
 * (`serviceCodes` ↔ this module's `offeredServiceCodes`). No open question remains on which
 * endpoint or field names this screen uses.
 *
 * **Extended, 2026-10-14 (NEX-1151)**: two Claude Design mockup pieces this screen was still
 * missing. (1) The "Condiciones que imponés por defecto" card — the three
 * `minContractMonths`/`noticeDays`/`quoteValidityDays` fields, editable here and sent on the same
 * `PATCH` as the checkbox selection. (2) "Agregar otra opción" under the checklist's own "LISTA
 * ADMINISTRABLE" note — a free-text suggestion POSTed to `/storage/service-options/suggest`,
 * deliberately NOT folded into `options`/`selectedCodes`: it is a suggestion for Nexvia to review,
 * not an instant catalog entry (see `suggestServiceOption`'s own docblock).
 *
 * NEX-1164 (2026-10-15): `FormPage` in three sections (spec S5) — section titles are `<legend>`s,
 * not headings, and the `Scales` glyph (never confirmed against the mockup) is gone (D-5.1).
 *
 * NEX-1165 — the catalog's `kind` (NEX-1166): "Servicios que ofrecés" lists only `kind` 'service';
 * the catalog's conditions (`kind` 'condition') are checkboxes in "Condiciones que imponés por
 * defecto", above the three numeric fields. Both are saved together in `offeredServiceCodes` (the
 * API's `PATCH /storage/me/services` accepts exactly those two kinds). The client's storage types
 * (`kind` 'storage_type') are never listed: `getServiceOptions()` does not even return them.
 *
 * **NEX-1164 design pass (2026-10-18, spec §3.5)**: the mock's narrow centred column (a wrapper
 * div: `Page` already emits its own `max-w-[1440px]`, and two arbitrary max-widths on one element
 * would be decided by stylesheet order), its copy, the "Agregar otra opción" field with the label
 * on the border, and no "LISTA ADMINISTRABLE" `SectionTitle` — that was one of the mock's dashed
 * annotation boxes, a note for the team, never UI. The default conditions read as `KeyValueList`
 * rows; "Editar" (local `editingConditions`, decision D-5.1) shows the same three inputs, saved by
 * the same "Guardar cambios". The mock's icons in the card titles are not drawn: `FormSection.title`
 * is a `string` (the `<legend>`) in @nexvia-logistics/ui 1.26.0.
 */
export default function ServiciosPage() {
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [options, setOptions] = useState<ServiceOption[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<ReadonlySet<string>>(new Set());
  const [conditions, setConditions] = useState('');
  const [minContractMonths, setMinContractMonths] = useState<number | null>(null);
  const [noticeDays, setNoticeDays] = useState<number | null>(null);
  const [quoteValidityDays, setQuoteValidityDays] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  // D-5.1: the three default conditions start as read-only rows; "Editar" swaps in the inputs.
  const [editingConditions, setEditingConditions] = useState(false);

  // "Agregar otra opción" (suggestion) is its own independent action against its own endpoint —
  // separate loading/error/confirmation state from the save button above, on purpose: submitting a
  // suggestion must never look like (or require) saving the rest of this form.
  const [suggestionLabel, setSuggestionLabel] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [justSuggested, setJustSuggested] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoad({ status: 'loading' });

    Promise.all([getServiceOptions(), getMyServices()])
      .then(([catalog, mine]) => {
        if (cancelled) return;
        // Defensive: a backend/contract mismatch (wrong envelope, unexpected shape) must land on
        // the existing 'error' state below, not crash `options.map`/`selectedCodes` construction —
        // this is exactly how this screen went blank/crashed on 2026-10-14 against a mock-only-
        // verified assumption about the response shape.
        if (!Array.isArray(catalog) || !Array.isArray(mine.offeredServiceCodes)) {
          setLoad({ status: 'error' });
          return;
        }
        setOptions(catalog);
        // NEX-1165 — only the codes this catalog lists: one saved earlier and since deactivated (or
        // moved to another kind) has no checkbox here to uncheck, and the API refuses a save carrying
        // it (`assertKnownServiceCodes`). Same filter QuoteForm applies.
        const listed = new Set(catalog.map((option) => option.code));
        setSelectedCodes(new Set(mine.offeredServiceCodes.filter((code) => listed.has(code))));
        setConditions(mine.serviceConditions);
        // `?? null`: `StorageServicesSelection` marks these three optional for OTHER consumers
        // that have no use for them (DepositosPage.tsx reads this same getMyServices() for
        // offeredServiceCodes alone — see that field's own docblock) — this screen always wants a
        // real `number | null`, never `undefined`, to hand its own number inputs below.
        setMinContractMonths(mine.minContractMonths ?? null);
        setNoticeDays(mine.noticeDays ?? null);
        setQuoteValidityDays(mine.quoteValidityDays ?? null);
        setLoad({ status: 'ready' });
      })
      .catch(() => {
        if (cancelled) return;
        setLoad({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const toggleOption = (code: string) => {
    setJustSaved(false);
    setSelectedCodes((current) => {
      const next = new Set(current);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleConditionsChange = (value: string) => {
    setJustSaved(false);
    setConditions(value);
  };

  const handleMinContractMonthsChange = (value: string) => {
    setJustSaved(false);
    setMinContractMonths(parseConditionFieldInput(value));
  };

  const handleNoticeDaysChange = (value: string) => {
    setJustSaved(false);
    setNoticeDays(parseConditionFieldInput(value));
  };

  const handleQuoteValidityDaysChange = (value: string) => {
    setJustSaved(false);
    setQuoteValidityDays(parseConditionFieldInput(value));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSaveError(null);
    setJustSaved(false);
    try {
      await updateMyServices({
        offeredServiceCodes: Array.from(selectedCodes),
        serviceConditions: conditions,
        minContractMonths,
        noticeDays,
        quoteValidityDays
      });
      setJustSaved(true);
    } catch (err) {
      setSaveError(describeServicesError(err, SAVE_ERROR_MESSAGE));
    } finally {
      setSaving(false);
    }
  };

  const handleSuggestionLabelChange = (value: string) => {
    setJustSuggested(false);
    setSuggestionLabel(value);
  };

  /**
   * "Agregar otra opción" — a plain `type="button"` action, not this page's form submission: this
   * whole screen is already one `<form>` (the one `FormPage` renders around `handleSubmit`, sent by
   * the "Guardar cambios" button), so a nested `<form>` here isn't valid HTML. Trimmed client-side
   * before sending, same guard `QuoteForm.tsx`'s own `addService` uses — the backend trims too, but
   * this also skips a pointless request for a whitespace-only draft.
   */
  const handleSuggestOption = async () => {
    const label = suggestionLabel.trim();
    if (!label) return;
    setSuggesting(true);
    setSuggestionError(null);
    setJustSuggested(false);
    try {
      await suggestServiceOption(label);
      setSuggestionLabel('');
      setJustSuggested(true);
    } catch (err) {
      setSuggestionError(describeServicesError(err, SUGGESTION_ERROR_MESSAGE));
    } finally {
      setSuggesting(false);
    }
  };

  /**
   * Without this, pressing Enter in the suggestion input would implicitly submit the page's OTHER
   * form control — "Guardar cambios" is the form's only `type="submit"` button, and HTML's implicit
   * submission fires it for Enter in ANY single-line text field inside the same form, not just one
   * near it. Same fix QuoteForm.tsx's own `handleServiceKeyDown` applies for its chip input.
   */
  const handleSuggestionKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSuggestOption();
    }
  };

  const serviceOptions = serviceOptionsOnly(options);
  const conditionOptions = conditionOptionsOnly(options);

  if (load.status === 'loading') {
    return (
      <Page className="py-6">
        <p className="m-0 flex items-center gap-2 text-ui-body text-on-surface-variant">
          <Spinner size={18} label={null} />
          Cargando servicios y condiciones…
        </p>
      </Page>
    );
  }

  if (load.status === 'error') {
    return (
      <Page className="py-6">
        <Alert tone="critical">{LOAD_ERROR_MESSAGE}</Alert>
        <div>
          <Button
            type="button"
            variant="outline"
            size="xs"
            showArrow={false}
            onClick={() => setReloadToken((value) => value + 1)}
          >
            Reintentar
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[44rem]">
      <FormPage
        title="Servicios y condiciones"
        description="Lo que ofrecés queda disponible para todas tus cotizaciones. El usuario filtra por estos servicios."
        onSubmit={handleSubmit}
        submitting={saving}
        notice={saveError ? <Alert tone="critical">{saveError}</Alert> : undefined}
        sections={[
          {
            id: 'servicios',
            title: 'Servicios que ofrecés',
            children: (
              <>
                {serviceOptions.length === 0 ? (
                  <p className="m-0 text-ui-body text-on-surface-placeholder">Todavía no hay servicios cargados para elegir.</p>
                ) : (
                  <CheckGroup>
                    {serviceOptions.map((option) => (
                      <CheckRow key={option.code} checked={selectedCodes.has(option.code)} onCheckedChange={() => toggleOption(option.code)}>
                        {option.label}
                      </CheckRow>
                    ))}
                  </CheckGroup>
                )}

                <div className="flex flex-col gap-2">
                  <FormField label="Agregar otra opción" labelPosition="border">
                    {(field) => (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          {...field}
                          type="text"
                          className="min-w-[12rem] flex-1"
                          value={suggestionLabel}
                          onChange={(event) => handleSuggestionLabelChange(event.target.value)}
                          onKeyDown={handleSuggestionKeyDown}
                          placeholder="Ej: control de plagas trimestral"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          showArrow={false}
                          disabled={suggesting || !suggestionLabel.trim()}
                          onClick={handleSuggestOption}
                          className="shrink-0"
                        >
                          {suggesting ? 'Agregando…' : 'Agregar'}
                        </Button>
                      </div>
                    )}
                  </FormField>
                  {suggestionError && <Alert tone="critical">{suggestionError}</Alert>}
                  {justSuggested && (
                    <span role="status" className="text-ui-body text-on-surface-variant">
                      Sugerencia enviada.
                    </span>
                  )}
                </div>
              </>
            )
          },
          {
            id: 'condiciones',
            title: 'Condiciones que imponés por defecto',
            children: (
              <>
                {conditionOptions.length > 0 && (
                  <CheckGroup>
                    {conditionOptions.map((option) => (
                      <CheckRow key={option.code} checked={selectedCodes.has(option.code)} onCheckedChange={() => toggleOption(option.code)}>
                        {option.label}
                      </CheckRow>
                    ))}
                  </CheckGroup>
                )}
                {!editingConditions ? (
                  <div className="flex flex-wrap items-start gap-3">
                    <KeyValueListStacked
                      className="min-w-0 flex-[1_1_14rem]"
                      items={[
                        {
                          term: 'Mínimo de contratación',
                          value: conditionValueText(minContractMonths, 'mes', 'meses', 'Sin mínimo')
                        },
                        { term: 'Preaviso para la baja', value: conditionValueText(noticeDays, 'día', 'días', 'Sin preaviso') },
                        {
                          term: 'Vigencia de las cotizaciones',
                          value: conditionValueText(quoteValidityDays, 'día', 'días', 'Sin vigencia')
                        }
                      ]}
                    />
                    <Button
                      type="button"
                      variant="text"
                      size="xs"
                      showArrow={false}
                      aria-label="Editar condiciones"
                      onClick={() => setEditingConditions(true)}
                      className="shrink-0"
                    >
                      <PencilSimple size={14} />
                      Editar
                    </Button>
                  </div>
                ) : (
                  <>
                    <FormField label="Mínimo de contratación" hint="En meses">
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          step={1}
                          value={minContractMonths ?? ''}
                          onChange={(event) => handleMinContractMonthsChange(event.target.value)}
                        />
                      )}
                    </FormField>
                    <FormField label="Preaviso para la baja" hint="En días">
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          step={1}
                          value={noticeDays ?? ''}
                          onChange={(event) => handleNoticeDaysChange(event.target.value)}
                        />
                      )}
                    </FormField>
                    <FormField label="Vigencia de las cotizaciones" hint="En días">
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          step={1}
                          value={quoteValidityDays ?? ''}
                          onChange={(event) => handleQuoteValidityDaysChange(event.target.value)}
                        />
                      )}
                    </FormField>
                  </>
                )}
                <p className="m-0 text-ui-small text-on-surface-variant">
                  Se proponen al cotizar y podés cambiarlas en cada solicitud. El usuario las ve junto al precio.
                </p>
              </>
            )
          },
          {
            id: 'habituales',
            title: 'Condiciones habituales',
            children: (
              <FormField label="Condiciones habituales (opcional)">
                {(field) => (
                  <Textarea
                    {...field}
                    value={conditions}
                    onChange={(event) => handleConditionsChange(event.target.value)}
                    rows={4}
                    placeholder="Ej.: depósito de garantía de un mes, preaviso de 30 días para el retiro, etc."
                  />
                )}
              </FormField>
            )
          }
        ]}
        actions={
          <StickyActionBar info={justSaved ? <span role="status">Cambios guardados.</span> : undefined}>
            <Button type="submit" variant="primary" size="xs" showArrow={false} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </StickyActionBar>
        }
      />
    </div>
  );
}
